/**
 * Dev mode — powers the password-protected `/dev` admin / builder route.
 *
 * `DEV_MODE` is true whenever the page is served under the `/dev` path (the
 * builder itself AND any preview it renders, including inside its iframe, since
 * the iframe is same-origin under `/dev`). It is computed once from the pathname
 * at module load.
 *
 * When DEV_MODE is on:
 *   - the mock API adapter is force-installed (so mock slugs work even in a
 *     production/staging build — see axios-instance.js),
 *   - Google Analytics is NOT initialised (see App.js),
 *   - every impression write (updateStatus) is suppressed and instead pushed to
 *     the in-page dev log (so nothing reaches the real backend), and
 *   - the dev HUD / builder UI is shown (see DevRoot).
 *
 * This is an INTERNAL tool. The gate is a convenience lock (sessionStorage), not
 * a security boundary — never put secrets behind it.
 */

export const DEV_MODE = /^\/(dev|admin)(\/|$)/.test(window.location.pathname);

export const DEV_PASSWORD = 'ACTV8me2day!';

const AUTH_KEY = 'a8_dev_auth';

export const isDevAuthed = () => {
  try {
    return sessionStorage.getItem(AUTH_KEY) === 'ok';
  } catch (e) {
    return false;
  }
};

export const setDevAuthed = (ok) => {
  try {
    if (ok) sessionStorage.setItem(AUTH_KEY, 'ok');
    else sessionStorage.removeItem(AUTH_KEY);
  } catch (e) {
    /* sessionStorage unavailable (private mode) — gate just won't persist */
  }
};

/* ---- In-page event log (feeds the dev HUD) ------------------------------- */

export const DEV_LOG_EVENT = 'a8-dev-log';

const STATUS_MEANING = {
  1: 'View',
  2: 'Save / accept',
  4: 'Redeem-ready',
  5: 'Get Offer click',
  6: 'Removed',
  8: 'Click-through',
};

export const statusMeaning = (n) => STATUS_MEANING[n] || `status ${n}`;

/**
 * Emit a structured event to the in-page dev log. No-op outside dev mode so call
 * sites can stay unconditional-ish. The HUD listens on `window` for DEV_LOG_EVENT.
 */
export const devLog = (entry) => {
  if (!DEV_MODE) return;
  const detail = { id: `${entry.type || 'evt'}-${performance.now()}`, ...entry };
  try {
    window.dispatchEvent(new CustomEvent(DEV_LOG_EVENT, { detail }));
  } catch (e) {
    /* CustomEvent unsupported — fall through to console only */
  }
  // eslint-disable-next-line no-console
  console.info(
    `%c[dev]%c ${entry.label || entry.type || 'event'}`,
    'color:#fff;background:#0B6FE8;padding:1px 4px;border-radius:3px',
    'color:#0B6FE8',
    entry
  );
};

/** Convenience: record a suppressed impression write. */
export const devLogImpression = (status) =>
  devLog({
    type: 'impression',
    status: Number(status),
    label: `impression status=${status} (${statusMeaning(Number(status))}) — suppressed`,
    suppressed: true,
  });
